// Standalone SVG rendering of a plan - used for the pohjakuva.png quote
// attachment and for preset thumbnails (data: URI). Pure string building;
// svgToPngBlob is the only browser-dependent function in utils/laskuri.
import { planBBox } from "./geometry.js";
import { formatFi } from "./planModel.js";

const esc = (v) => String(v)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const SHAPE_FILL = "#e8e4da";
const SHAPE_STROKE = "#4a4a4a";
const DRAIN_COLOR = "#2a7ab8";
const TEXT = (x, y, anchor, size, content) =>
  `<text x="${x}" y="${y}" text-anchor="${anchor}" font-size="${size}" fill="#333333" font-family="sans-serif">${content}</text>`;

export function planToSvgString(plan, { widthPx = 800, caption = "", lang = "fi", pillarWord = "Pilari" } = {}) {
  const bbox = planBBox(plan.shapes || []);
  if (!bbox) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="200" viewBox="0 0 ${widthPx} 200"><rect width="100%" height="100%" fill="#ffffff"/></svg>`;
  }
  const pad = 0.5; // meters of margin around the plan
  const w = bbox.maxX - bbox.minX + 2 * pad;
  const h = bbox.maxY - bbox.minY + 2 * pad;
  const scale = widthPx / w;
  const heightPx = Math.round(h * scale) + (caption ? 40 : 0);
  const X = (x) => ((x - bbox.minX + pad) * scale).toFixed(1);
  const Y = (y) => ((y - bbox.minY + pad) * scale).toFixed(1);
  const parts = [];

  for (const s of plan.shapes) {
    if (s.type === "rect") {
      parts.push(`<rect x="${X(s.x)}" y="${Y(s.y)}" width="${(s.w * scale).toFixed(1)}" height="${(s.h * scale).toFixed(1)}" fill="${SHAPE_FILL}" stroke="${SHAPE_STROKE}" stroke-width="2"/>`);
    } else if (s.type === "tri") {
      parts.push(`<polygon points="${s.points.map(([x, y]) => `${X(x)},${Y(y)}`).join(" ")}" fill="${SHAPE_FILL}" stroke="${SHAPE_STROKE}" stroke-width="2"/>`);
    } else if (s.type === "circle") {
      parts.push(`<circle cx="${X(s.cx)}" cy="${Y(s.cy)}" r="${(s.r * scale).toFixed(1)}" fill="${SHAPE_FILL}" stroke="${SHAPE_STROKE}" stroke-width="2"/>`);
    } else if (s.type === "pillar") {
      parts.push(`<circle cx="${X(s.cx)}" cy="${Y(s.cy)}" r="${(s.r * scale).toFixed(1)}" fill="#cfc8ba" stroke="${SHAPE_STROKE}" stroke-width="2"/>`);
      parts.push(`<circle cx="${X(s.cx)}" cy="${Y(s.cy)}" r="3" fill="${SHAPE_STROKE}"/>`);
    }
  }
  for (const s of plan.shapes) {
    if (s.type === "rect") {
      parts.push(TEXT(X(s.x + s.w / 2), Y(s.y) - 6, "middle", 14, `${formatFi(s.w, 2, lang)} m`));
      parts.push(TEXT(X(s.x) - 6, Y(s.y + s.h / 2), "end", 14, `${formatFi(s.h, 2, lang)} m`));
    } else if (s.type === "circle") {
      parts.push(TEXT(X(s.cx), Y(s.cy), "middle", 14, `⌀ ${formatFi(s.r * 2, 2, lang)} m`));
    } else if (s.type === "pillar") {
      parts.push(TEXT(X(s.cx), Number(Y(s.cy + s.r)) + 16, "middle", 13, `${esc(pillarWord)} ⌀ ${Math.round(s.r * 200)} cm × ${formatFi(s.height, 1, lang)} m`));
    }
  }
  for (const d of plan.drains || []) {
    parts.push(`<circle cx="${X(d.x)}" cy="${Y(d.y)}" r="${(d.radius * scale).toFixed(1)}" fill="none" stroke="${DRAIN_COLOR}" stroke-width="1.5" stroke-dasharray="6 4"/>`);
    parts.push(`<circle cx="${X(d.x)}" cy="${Y(d.y)}" r="6" fill="${DRAIN_COLOR}"/>`);
  }
  if (caption) parts.push(TEXT(widthPx / 2, heightPx - 14, "middle", 16, esc(caption)));

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="${heightPx}" viewBox="0 0 ${widthPx} ${heightPx}"><rect width="100%" height="100%" fill="#ffffff"/>${parts.join("")}</svg>`;
}

/** Browser-only (Image + canvas). Resolves to a PNG Blob; rejects on failure. */
export function svgToPngBlob(svgString, widthPx = 1200) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = img.height / img.width || 1;
        const canvas = document.createElement("canvas");
        canvas.width = widthPx;
        canvas.height = Math.round(widthPx * ratio);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((png) => (png ? resolve(png) : reject(new Error("canvas.toBlob returned null"))), "image/png");
      } catch (e) {
        reject(e);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("SVG image load failed"));
    };
    img.src = url;
  });
}
